if (!customElements.get('ugc-video-carousel')) {
  customElements.define(
    'ugc-video-carousel',
    class extends HTMLElement {
      connectedCallback() {
        this.carousel = this.querySelector('carousel-component');
        this.addEventListener('click', this.handleClick);
        this.watchForPosters();
        this.waitForEmbla();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.embla?.off('select', this.handleSelect);
        this.embla?.off('pointerDown', this.handlePointerDown);
        this.embla?.off('pointerUp', this.handlePointerUp);
        this.posterObserver?.disconnect();
        this.posterObserver = null;
        if (this.pendingAutoplay) {
          this.pendingAutoplay.video.removeEventListener('canplay', this.pendingAutoplay.handler);
          this.pendingAutoplay = null;
        }
      }

      // A <video poster> is fetched the moment the tag is parsed, viewport be damned, so
      // ugc-video-card.liquid defers the full-size poster into `data-ugc-poster` and we promote it
      // once the section is close. Without this, 11 cards pulled 936KB of offscreen frames.
      //
      // The attribute name MUST stay namespaced. The Instafeed app runs its own IntersectionObserver
      // that promotes `data-poster` -> `poster`; when we used that plain name, Instafeed claimed our
      // videos and — after we removed the attribute — wrote `poster="null"`, blanking the cards.
      watchForPosters() {
        if (!('IntersectionObserver' in window)) {
          this.postersReached = true;
          this.upgradePosters();
          return;
        }
        this.posterObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.posterObserver?.disconnect();
            this.posterObserver = null;
            this.postersReached = true;
            this.upgradePosters();
          },
          { rootMargin: '300px 0px' }
        );
        this.posterObserver.observe(this);
      }

      // Promote every card that is actually on screen, plus one either side so a swipe reveals a
      // sharp poster rather than the placeholder.
      //
      // This must be measured, not assumed: how many cards are visible depends on the breakpoint —
      // 3 on mobile but 7 on desktop. A fixed selected±1 left 4 visible desktop cards stuck on
      // Shopify's 56x100 `_small` poster, upscaled ~10x into a 484px box. And the poster is what you
      // see the whole time a card sits paused: a never-played <video> keeps showing its poster even
      // after the data has loaded, so poster quality IS the perceived quality until you hit play.
      //
      // Cost is only paid once the section is reached, long after LCP, so widening this does not
      // affect page load.
      upgradePosters() {
        if (!this.postersReached) return;
        const slides = this.slides;
        if (!slides.length) return;

        const viewport = this.querySelector('[data-carousel-viewport]');
        const bounds = viewport ? viewport.getBoundingClientRect() : null;
        const left = bounds ? Math.max(bounds.left, 0) : 0;
        const right = bounds ? Math.min(bounds.right, window.innerWidth) : window.innerWidth;

        const onScreen = slides.map((slide) => {
          if (!bounds) return true;
          const r = slide.getBoundingClientRect();
          return r.right > left + 2 && r.left < right - 2;
        });

        const count = slides.length;
        const wanted = new Set();
        onScreen.forEach((visible, index) => {
          if (!visible) return;
          wanted.add(index);
          wanted.add((index - 1 + count) % count);
          wanted.add((index + 1) % count);
        });
        // Nothing measurable (display:none, not laid out yet) — fall back to the selected card.
        if (!wanted.size) {
          const selected = this.embla ? this.embla.selectedScrollSnap() : 0;
          [selected - 1, selected, selected + 1].forEach((raw) => wanted.add(((raw % count) + count) % count));
        }

        wanted.forEach((index) => {
          const video = slides[index]?.querySelector('video[data-ugc-poster]');
          const poster = video?.dataset.ugcPoster;
          if (!poster) return;
          video.setAttribute('poster', poster);
          video.removeAttribute('data-ugc-poster');
        });
      }

      handlePointerDown = () => {
        this.downSnap = this.embla.selectedScrollSnap();
      };

      handlePointerUp = () => {
        if (this.embla.selectedScrollSnap() === this.downSnap) {
          this.embla.internalEngine().scrollBody.useDuration(32).useFriction(0);
        }
      };

      waitForEmbla(attempt = 0) {
        const embla = this.carousel?.embla;
        if (embla) {
          this.embla = embla;
          this.carousel.reInit({ watchResize: false, duration: 32 });
          this.embla.on('select', this.handleSelect);
          this.embla.on('pointerDown', this.handlePointerDown);
          this.embla.on('pointerUp', this.handlePointerUp);
          this.handleSelect();
          this.autoplayActive();
          return;
        }
        if (attempt < 40) setTimeout(() => this.waitForEmbla(attempt + 1), 50);
      }

      autoplayActive() {
        if (this.classList.contains('is-editor-mode')) return;
        const slide = this.slides[this.embla.selectedScrollSnap()];
        const video = slide?.querySelector('video');
        if (!video) return;
        if (slide.querySelector('lazy-video') && video.readyState < 3) {
          this.pendingAutoplay = { video, handler: () => this.playActive(slide) };
          video.addEventListener('canplay', this.pendingAutoplay.handler, { once: true });
        } else {
          this.playActive(slide);
        }
      }

      playActive(slide) {
        if (!slide.classList.contains('is-active')) return;
        const video = slide.querySelector('video');
        if (!video) return;
        this.setMuted(slide, true);
        const playing = video.play();
        if (playing) {
          playing
            .then(() => {
              slide.classList.add('is-playing');
              this.setToggleLabel(slide, 'pause');
            })
            .catch(() => {});
        }
      }

      get slides() {
        return [...this.querySelectorAll('[data-ugc-slide]')];
      }

      handleClick = (event) => {
        const slide = event.target.closest('[data-ugc-slide]');
        if (!slide) return;
        const index = this.slides.indexOf(slide);
        const toggle = event.target.closest('[data-ugc-toggle]');
        if (toggle) {
          event.preventDefault();
          this.toggleVideo(slide, index);
          return;
        }
        if (event.target.closest('a')) return;
        if (event.target.closest('.ugc-video-card__media')) {
          this.toggleVideo(slide, index);
          return;
        }
        if (!slide.classList.contains('is-active')) this.embla?.scrollTo(index);
      };

      toggleVideo(slide, index) {
        const video = slide.querySelector('video');
        if (!video) return;
        if (video.paused) {
          this.pauseAll();
          this.setMuted(slide, true);
          video.play();
          slide.classList.add('is-playing');
          this.setToggleLabel(slide, 'pause');
          this.embla?.scrollTo(index);
        } else {
          video.pause();
          slide.classList.remove('is-playing');
          this.setToggleLabel(slide, 'play');
        }
      }

      setMuted(slide, muted) {
        const video = slide.querySelector('video');
        if (!video) return;
        video.muted = muted;
        slide.classList.toggle('is-unmuted', !muted);
      }

      pauseAll() {
        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (video && !video.paused) video.pause();
          slide.classList.remove('is-playing');
          this.setToggleLabel(slide, 'play');
        });
      }

      setToggleLabel(slide, state) {
        const toggle = slide.querySelector('[data-ugc-toggle]');
        if (toggle) toggle.setAttribute('aria-label', this.dataset[state + 'Label'] || state);
      }

      handleSelect = () => {
        this.setActive(this.embla.selectedScrollSnap());
        this.upgradePosters();
      };

      setActive(selected) {
        const slides = this.slides;
        const count = slides.length;
        const looped = this.embla?.internalEngine().options.loop;
        slides.forEach((slide, index) => {
          const isActive = index === selected;
          slide.classList.toggle('is-active', isActive);
          if (isActive) {
            slide.classList.remove('is-left', 'is-right');
          } else {
            let onRight;
            if (looped) {
              const forward = (index - selected + count) % count;
              onRight = forward <= count - forward;
            } else {
              onRight = index > selected;
            }
            slide.classList.toggle('is-right', onRight);
            slide.classList.toggle('is-left', !onRight);
          }
          if (!isActive && slide.classList.contains('is-playing')) {
            slide.querySelector('video')?.pause();
            slide.classList.remove('is-playing');
            this.setToggleLabel(slide, 'play');
          }
        });
      }
    }
  );
}
